import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';

function AddUser({ open, onClose, onSave, userRole, newUserData }) {
    // Initialize newUser state with newUserData from parent if available
    const [newUser, setNewUser] = useState(newUserData || {
        username: '',
        password: '',
        role: 'STUDENT',  // Default to 'STUDENT' role
        studentId: '',
    });

    const [errors, setErrors] = useState({});

    // Update newUser state whenever newUserData changes (e.g., when AddUser dialog is opened)
    useEffect(() => {
        if (open && newUserData) {
            setNewUser(newUserData);
        }
    }, [open]);



    const handleChange = (event) => {
        setNewUser({
            ...newUser,
            [event.target.name]: event.target.value
        });
        setErrors({
            ...errors,
            [event.target.name]: ''
        });
    };

    const validateFields = () => {
        const validationErrors = {};
        if (!newUser.username) {
            validationErrors.username = 'Username is required.';
        }
        if (!newUser.password) {
            validationErrors.password = 'Password is required.';
        }
        if (!newUser.role) {
            validationErrors.role = 'Role is required.';
        }
        return validationErrors;
    };

    const handleSave = async () => {
        const validationErrors = validateFields();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        // Only allow admin to create admin or staff users
        if (userRole !== 'ROLE_ADMIN' && (newUser.role === 'ADMIN' || newUser.role === 'STAFF')) {
            alert("You do not have permission to create admin or staff accounts.");
            return;
        }

        // Call onSave function passed as prop (from Students.js) to save the new user
        const success = await onSave(newUser);
        if (success) {
            onClose(); // Close the dialog on successful save
        }
    };

    const roleOptions = userRole === 'ROLE_ADMIN'
        ? ['STUDENT', 'STAFF', 'ADMIN']
        : ['STUDENT'];  // If not admin, default to STUDENT

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add New User</DialogTitle>
            <DialogContent sx={{ width: '800px' }}>
                <Stack spacing={2} mt={1}>
                    <div>
                        {errors.username && (
                            <Typography variant="body2" color="error" gutterBottom>
                                {errors.username}
                            </Typography>
                        )}
                        <TextField
                            label="Username"
                            name="username"
                            value={newUser.username}
                            onChange={handleChange}
                            fullWidth
                        />
                    </div>

                    <div>
                        {errors.password && (
                            <Typography variant="body2" color="error" gutterBottom>
                                {errors.password}
                            </Typography>
                        )}
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={newUser.password}
                            onChange={handleChange}
                            fullWidth
                        />
                    </div>

                    <div>
                        {errors.role && (
                            <Typography variant="body2" color="error" gutterBottom>
                                {errors.role}
                            </Typography>
                        )}
                        <FormControl fullWidth>
                            <InputLabel>Role</InputLabel>
                            <Select
                                name="role"
                                value={newUser.role || ''}
                                onChange={handleChange}
                                label="Role"
                            >
                                {roleOptions.map(role => (
                                    <MenuItem key={role} value={role}>{role}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div>
                        {errors.studentId && (
                            <Typography variant="body2" color="error" gutterBottom>
                                {errors.studentId}
                            </Typography>
                        )}
                        <TextField
                            label="Student ID"
                            name="studentId"
                            value={newUser.studentId}
                            onChange={handleChange}
                            fullWidth
                            disabled
                        />
                    </div>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleSave}
                    variant="contained"
                    color="primary"
                >Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddUser;
